import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import {
  markerBox,
  mitFarbe,
  mitGroesse,
  overlayInnerSvg,
  OverlayLeseFehler,
  skaliereMarker,
  svgToOverlay,
  verschiebeMarker,
  type Marker,
  type Overlay,
  type Punkt,
  type SizeStep,
} from '../../domain/annotation';
import { useT } from '../../i18n';
import { overlayPfad, readBase64, readText } from '../../platform/storage';
import { Icon } from '../../ui/Icon';
import { sichereOverlay } from './annotateSave';
import styles from './AnnotateSheet.module.css';
import { editorReducer, naechsteNummer, startZustand } from './reducer';
import { FARBEN, Toolbar, type Werkzeug } from './Toolbar';

/**
 * Ein Foto in Grossansicht, mit seiner Markierungsebene darüber.
 *
 * **Zwei Ebenen, ein Massstab:** das `<img>` und das `<svg>` liegen deckungsgleich in einer
 * Bühne, die das Seitenverhältnis des Bildes trägt. Alle Marken stehen in Bildpixeln, die
 * `viewBox` trägt die Bildgrösse — dreht der Prüfer das iPad, skaliert der Browser beides
 * gemeinsam, ohne dass hier etwas nachgerechnet wird.
 *
 * **Gezeichnet wird mit demselben Markup, das später in der Datei steht** (`overlayInnerSvg`).
 * Eine zweite Zeichenroutine für den Bildschirm wäre eine zweite Quelle der Wahrheit und liefe
 * der gespeicherten Datei mit der Zeit davon.
 */

const neueId = () => Math.random().toString(36).slice(2, 10);

/** Abbild des geladenen Standes: der Vergleich beim Schliessen braucht eine eigene Kopie. */
const abbild = (o: Overlay): Overlay => JSON.parse(JSON.stringify(o)) as Overlay;

/** Woran gerade gezogen wird. */
type Zug =
  | { art: 'zeichnen'; start: Punkt; marke: Marker }
  | { art: 'verschieben'; letzter: Punkt; id: string }
  | { art: 'skalieren'; anker: Punkt; start: Punkt; id: string; marke: Marker };

interface Props {
  /** Pfad des Fotos unterhalb von Documents. */
  path: string;
  taskId: string;
  onClose: () => void;
  /**
   * Meldet einen fehlgeschlagenen Schreibvorgang — die App zeigt dafür einen Toast.
   *
   * ⚠️ **Mit dem Fehler.** Bis zum 2026-09-24 kam er ohne, und damit stand am Gerät nur „konnte
   * nicht gesichert werden" — ohne die Ursache, die der Aufrufer ins Dateilog schreiben könnte.
   */
  onSaveFailed?: (fehler: unknown) => void;
}

export function AnnotateSheet({ path, taskId, onClose, onSaveFailed }: Props) {
  const t = useT();

  const [bild, setBild] = useState<string | null>(null);
  const [masse, setMasse] = useState<{ w: number; h: number } | null>(null);
  const [leseFehler, setLeseFehler] = useState(false);
  const [state, dispatch] = useReducer(editorReducer, startZustand({ width: 0, height: 0, markers: [] }));

  /** Der Stand beim Öffnen — die Vergleichsgrundlage für `sichereOverlay`. */
  const geladen = useRef<Overlay | null>(null);
  const zug = useRef<Zug | null>(null);

  const [werkzeug, setWerkzeug] = useState<Werkzeug>('arrow');
  const [farbe, setFarbe] = useState(FARBEN[0]);
  const [stufe, setStufe] = useState<SizeStep>('mittel');
  const [vorschau, setVorschau] = useState<Marker | null>(null);
  /**
   * Wohin der Text soll — entweder an eine neue Stelle (`{x,y}`) oder in eine **vorhandene**
   * Marke (`{id}`). Letzteres ist der Weg zum nachträglichen Ändern (Wunsch des Nutzers,
   * 2026-09-13): ein Doppeltipp auf eine Beschriftung öffnet dasselbe Feld, vorbelegt.
   */
  const [textFrage, setTextFrage] = useState<{ x: number; y: number } | { id: string } | null>(null);
  const [textEingabe, setTextEingabe] = useState('');

  /**
   * Laden: Bild **und** Ebene, danach erst anzeigen.
   *
   * ⚠️ **Die Reihenfolge ist keine Geschmacksfrage.** Wird die Fläche schon nach dem Bild
   * freigegeben und die Ebene erst danach nachgereicht, gibt es ein Fenster, in dem der
   * Prüfer bereits zeichnen kann — und das nachfolgende `ersetzen` wischt seine frischen
   * Marken lautlos weg. Schlimmer noch: `geladen.current` zeigte dann auf denselben Stand,
   * das Schliessen hielte die Ebene für unverändert und schriebe **gar nichts**.
   */
  useEffect(() => {
    let lebt = true;

    void (async () => {
      const daten = await readBase64(path);
      if (!lebt || !daten) return;
      const quelle = 'data:image/jpeg;base64,' + daten;

      const groesse = await new Promise<{ w: number; h: number } | null>((fertig) => {
        const img = new Image();
        img.onload = () => fertig({ w: img.naturalWidth, h: img.naturalHeight });
        // Ein unlesbares Bild ist kein Grund abzustürzen; die Ansicht bleibt beim Ladezeichen.
        img.onerror = () => fertig(null);
        img.src = quelle;
      });
      if (!lebt || groesse === null) return;

      let ebene: Overlay = { width: groesse.w, height: groesse.h, markers: [] };
      let unlesbar = false;
      try {
        const roh = await readText(overlayPfad(path));
        if (roh) ebene = svgToOverlay(roh);
      } catch (fehler) {
        // Nicht als „leer" durchwinken: beim Schliessen würde die Datei sonst gelöscht.
        if (fehler instanceof OverlayLeseFehler) unlesbar = true;
        // Keine Datei vorhanden ist der Normalfall — dann bleibt die Ebene leer.
      }
      if (!lebt) return;

      geladen.current = unlesbar ? null : abbild(ebene);
      dispatch({ typ: 'ersetzen', overlay: ebene });
      setLeseFehler(unlesbar);
      // Zuletzt: erst dieses Paar gibt die Fläche frei.
      setBild(quelle);
      setMasse(groesse);
    })();

    return () => {
      lebt = false;
    };
  }, [path]);

  const schliessen = useCallback(() => {
    if (leseFehler || masse === null) {
      onClose();
      return;
    }
    void sichereOverlay(path, taskId, geladen.current, state.overlay)
      .catch((fehler: unknown) => onSaveFailed?.(fehler))
      .finally(onClose);
  }, [leseFehler, masse, onClose, onSaveFailed, path, state.overlay, taskId]);

  if (masse === null || bild === null) {
    return (
      <div className={styles.blatt}>
        <div className={styles.raum}>
          <span className={styles.laedt}>…</span>
        </div>
      </div>
    );
  }

  const { w, h } = masse;

  /** Bildschirm → Bildkoordinaten. Die einzige Stelle, an der beide aufeinandertreffen. */
  const zuBild = (e: ReactPointerEvent<SVGSVGElement>): Punkt => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * w,
      y: ((e.clientY - r.top) / r.height) * h,
    };
  };

  const neueMarke = (p: Punkt): Marker | null => {
    const id = neueId();
    switch (werkzeug) {
      case 'arrow':
        return { id, kind: 'arrow', color: farbe, stroke: stufe, von: p, bis: p };
      case 'ellipse':
        return { id, kind: 'ellipse', color: farbe, stroke: stufe, cx: p.x, cy: p.y, rx: 0, ry: 0 };
      case 'rect':
        return { id, kind: 'rect', color: farbe, stroke: stufe, x: p.x, y: p.y, w: 0, h: 0 };
      case 'cover':
        return { id, kind: 'cover', color: farbe, x: p.x, y: p.y, w: 0, h: 0 };
      case 'freehand':
        return { id, kind: 'freehand', color: farbe, stroke: stufe, punkte: [p] };
      case 'number':
        return {
          id, kind: 'number', color: farbe, size: stufe, x: p.x, y: p.y,
          n: naechsteNummer(state.overlay.markers),
        };
      case 'text':
        // Text braucht erst eine Eingabe; die Marke entsteht nach dem Bestätigen.
        return null;
    }
  };

  const zieheMarke = (marke: Marker, start: Punkt, jetzt: Punkt): Marker => {
    switch (marke.kind) {
      case 'arrow':
        return { ...marke, bis: jetzt };
      case 'ellipse':
        return {
          ...marke,
          cx: (start.x + jetzt.x) / 2,
          cy: (start.y + jetzt.y) / 2,
          rx: Math.abs(jetzt.x - start.x) / 2,
          ry: Math.abs(jetzt.y - start.y) / 2,
        };
      case 'rect':
      case 'cover':
        return {
          ...marke,
          x: Math.min(start.x, jetzt.x),
          y: Math.min(start.y, jetzt.y),
          w: Math.abs(jetzt.x - start.x),
          h: Math.abs(jetzt.y - start.y),
        };
      case 'freehand':
        return { ...marke, punkte: [...marke.punkte, jetzt] };
      default:
        return marke;
    }
  };

  const down = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (leseFehler) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = zuBild(e);

    // Auf eine vorhandene Marke getippt? Dann wird sie ausgewählt und verschoben, statt eine
    // neue anzulegen — sonst könnte man eine Marke nie wieder anfassen.
    const getroffen = (e.target as Element).closest?.('[data-id]')?.getAttribute('data-id');
    if (getroffen) {
      // ⚠️ **Doppeltipp auf eine Beschriftung öffnet sie zum Ändern.** Ohne das liesse sich
      // ein Tippfehler nur durch Löschen und neu Setzen berichtigen — und die Marke verlöre
      // dabei ihre Stelle. `e.detail` zählt die Tipper; der Zeiger meldet es auf dem iPad
      // genauso wie die Maus im Browser.
      const marke = state.overlay.markers.find((m) => m.id === getroffen);
      if (e.detail >= 2 && marke?.kind === 'text') {
        dispatch({ typ: 'auswaehlen', id: getroffen });
        setTextFrage({ id: getroffen });
        setTextEingabe(marke.text);
        return;
      }
      dispatch({ typ: 'auswaehlen', id: getroffen });
      // Klammer auf: der ganze Zug bekommt **einen** Verlaufseintrag, nicht einen je
      // Zeigerbewegung (s. `reducer.ts`, `zug-start`).
      dispatch({ typ: 'zug-start' });
      zug.current = { art: 'verschieben', letzter: p, id: getroffen };
      return;
    }

    if (state.auswahl !== null) dispatch({ typ: 'auswaehlen', id: null });

    if (werkzeug === 'text') {
      setTextFrage(p);
      setTextEingabe('');
      return;
    }

    const marke = neueMarke(p);
    if (!marke) return;

    if (marke.kind === 'number') {
      // Eine Nummer wird gesetzt, nicht gezogen.
      dispatch({ typ: 'marke-hinzu', marke });
      return;
    }

    zug.current = { art: 'zeichnen', start: p, marke };
    setVorschau(marke);
  };

  const move = (e: ReactPointerEvent<SVGSVGElement>) => {
    const z = zug.current;
    if (!z) return;
    const p = zuBild(e);

    if (z.art === 'zeichnen') {
      const gezogen = zieheMarke(z.marke, z.start, p);
      zug.current = { ...z, marke: gezogen };
      setVorschau(gezogen);
      return;
    }

    if (z.art === 'verschieben') {
      const alt = state.overlay.markers.find((m) => m.id === z.id);
      if (!alt) return;
      dispatch({
        typ: 'marke-aendern',
        marke: verschiebeMarker(alt, p.x - z.letzter.x, p.y - z.letzter.y),
      });
      zug.current = { ...z, letzter: p };
      return;
    }

    // Skalieren: das Verhältnis zum Ankerpunkt bestimmt den Faktor. Sehr kleine Abstände
    // ergäben riesige Faktoren, deshalb der Mindestabstand.
    const breite = Math.max(Math.abs(z.start.x - z.anker.x), w * 0.01);
    const hoehe = Math.max(Math.abs(z.start.y - z.anker.y), h * 0.01);
    dispatch({
      typ: 'marke-aendern',
      marke: skaliereMarker(
        z.marke,
        Math.abs(p.x - z.anker.x) / breite,
        Math.abs(p.y - z.anker.y) / hoehe,
        z.anker,
      ),
    });
  };

  const up = () => {
    const z = zug.current;
    zug.current = null;
    setVorschau(null);
    if (z?.art === 'verschieben' || z?.art === 'skalieren') {
      // Klammer zu. Hat sich nichts bewegt, nimmt der Reducer den Eintrag wieder heraus.
      dispatch({ typ: 'zug-ende' });
      return;
    }
    if (z?.art !== 'zeichnen') return;

    // Ein blosser Tipp ohne Zug ergibt eine Marke ohne Ausdehnung — sie wäre unsichtbar und
    // liesse sich nie wieder anfassen. Sie wird deshalb verworfen.
    const kasten = markerBox(z.marke, h);
    const winzig = kasten.w < w * 0.01 && kasten.h < h * 0.01;
    if (winzig) return;

    dispatch({ typ: 'marke-hinzu', marke: z.marke });
  };

  const textUebernehmen = () => {
    const ziel = textFrage;
    setTextFrage(null);
    if (!ziel) return;
    const text = textEingabe.trim();

    if ('id' in ziel) {
      const alt = state.overlay.markers.find((m) => m.id === ziel.id);
      if (!alt || alt.kind !== 'text') return;
      // Leerer Text heisst „weg damit": eine Beschriftung ohne Buchstaben wäre eine
      // unsichtbare Marke, die man nur durch Zufall wiederfände.
      if (text === '') dispatch({ typ: 'marke-loeschen', id: alt.id });
      else dispatch({ typ: 'marke-aendern', marke: { ...alt, text } });
      return;
    }

    if (text === '') return;
    dispatch({
      typ: 'marke-hinzu',
      marke: {
        id: neueId(), kind: 'text', color: farbe, size: stufe,
        x: ziel.x, y: ziel.y, text,
      },
    });
  };

  const anzuzeigen: Overlay = vorschau
    ? { ...state.overlay, markers: [...state.overlay.markers, vorschau] }
    : state.overlay;

  const ausgewaehlt = state.overlay.markers.find((m) => m.id === state.auswahl) ?? null;
  const rahmen = ausgewaehlt ? markerBox(ausgewaehlt, h) : null;
  const griff = Math.max(w, h) * 0.018;

  return (
    <div className={styles.blatt} role="dialog" aria-modal="true" aria-label={t.annotate.title}>
      <div className={styles.kopf}>
        <span className={styles.titel}>{t.annotate.title}</span>
        <span className={styles.zaehler}>
          {t.annotate.markerCount(state.overlay.markers.length)}
        </span>
        <span className={styles.kopfLuecke} />
        <button type="button" className={`tap ${styles.fertig}`} onClick={schliessen}>
          <Icon name="check" size={17} />
          <span>{t.annotate.close}</span>
        </button>
      </div>

      <div className={styles.raum}>
        {/*
          **Das Bild liegt im SVG**, nicht darunter: als `<image>` in derselben `viewBox`.
          Damit sind Bild und Marken von Bauart her deckungsgleich — es gibt keine zweite Box,
          die beim Drehen anders rechnen könnte. Die Breiten- und Höhenattribute geben dem SVG
          eine Eigengrösse, sodass es sich wie ein Bild in den verfügbaren Platz einpasst.
        */}
        <svg
          className={styles.ebene}
          width={w}
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
        >
          <image href={bild} x={0} y={0} width={w} height={h} />
          {/*
              Dasselbe Markup wie in der Datei. Der Text darin ist bereits maskiert
              (`esc` in `domain/annotation.ts`), Farben stammen aus der festen Palette.
            */}
          <g dangerouslySetInnerHTML={{ __html: overlayInnerSvg(anzuzeigen) }} />

          {rahmen && (
              <g pointerEvents="none">
                {/*
                  Gestrichelter Rahmen, keine Füllung: im Bogen bedeutet eine Füllfarbe
                  „hier sind Punkte vergeben". Ein Rahmen ist mit nichts zu verwechseln.
                */}
                <rect
                  x={rahmen.x - griff}
                  y={rahmen.y - griff}
                  width={rahmen.w + griff * 2}
                  height={rahmen.h + griff * 2}
                  fill="none"
                  stroke="#f5f7fa"
                  strokeWidth={Math.max(w, h) * 0.003}
                  strokeDasharray={`${griff} ${griff * 0.7}`}
                />
                <circle
                  cx={rahmen.x + rahmen.w + griff}
                  cy={rahmen.y + rahmen.h + griff}
                  r={griff}
                  fill="#f5f7fa"
                  pointerEvents="all"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    if (!ausgewaehlt) return;
                    dispatch({ typ: 'zug-start' });
                    zug.current = {
                      art: 'skalieren',
                      anker: { x: rahmen.x, y: rahmen.y },
                      start: { x: rahmen.x + rahmen.w, y: rahmen.y + rahmen.h },
                      id: ausgewaehlt.id,
                      marke: ausgewaehlt,
                    };
                  }}
                />
              </g>
            )}
        </svg>
      </div>

      {textFrage && (
        <div className={styles.textFrage}>
          <div className={styles.textKasten}>
            <label htmlFor="annotate-text">{t.annotate.textPrompt}</label>
            <input
              id="annotate-text"
              autoFocus
              value={textEingabe}
              placeholder={t.annotate.textPlaceholder}
              onChange={(e) => setTextEingabe(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && textUebernehmen()}
            />
            <div className={styles.textKnoepfe}>
              <button type="button" className="tap" onClick={() => setTextFrage(null)}>
                {t.annotate.cancel}
              </button>
              <button type="button" className={`tap ${styles.betont}`} onClick={textUebernehmen}>
                {t.annotate.ok}
              </button>
            </div>
          </div>
        </div>
      )}

      {leseFehler && <div className={styles.hinweis}>{t.annotate.readError}</div>}

      <Toolbar
        werkzeug={werkzeug}
        farbe={farbe}
        stufe={stufe}
        kannUndo={state.verlauf.length > 0}
        kannRedo={state.zukunft.length > 0}
        hatAuswahl={state.auswahl !== null}
        // Ohne Auswahl gilt die Vorgabe fürs Nächste — dann wirkt die Grösse immer. Mit
        // Auswahl nur, wenn diese Marke überhaupt eine Grösse hat (die Deckfläche hat keine).
        groesseWirkt={!ausgewaehlt || mitGroesse(ausgewaehlt, stufe) !== null}
        hatMarken={state.overlay.markers.length > 0}
        gesperrt={leseFehler}
        onWerkzeug={setWerkzeug}
        onFarbe={(f) => {
          setFarbe(f);
          if (ausgewaehlt) dispatch({ typ: 'marke-aendern', marke: mitFarbe(ausgewaehlt, f) });
        }}
        onStufe={(st) => {
          setStufe(st);
          // ⚠️ **Bis zum 2026-09-13 wirkte die Grösse nur auf die *nächste* Marke.** Wer eine
          // vorhandene antippte und dann „groß" wählte, sah nichts geschehen — der Nutzer
          // hielt die Knöpfe deshalb für kaputt. Jetzt ändert sich die ausgewählte Marke mit,
          // und zwar über denselben Reducer, damit `Rückgängig` es zurücknehmen kann.
          const geaendert = ausgewaehlt && mitGroesse(ausgewaehlt, st);
          if (geaendert) dispatch({ typ: 'marke-aendern', marke: geaendert });
        }}
        onUndo={() => dispatch({ typ: 'undo' })}
        onRedo={() => dispatch({ typ: 'redo' })}
        onMarkeLoeschen={() => state.auswahl && dispatch({ typ: 'marke-loeschen', id: state.auswahl })}
        onAllesLoeschen={() => {
          if (window.confirm(t.annotate.clearAllAsk)) dispatch({ typ: 'alles-loeschen' });
        }}
      />
    </div>
  );
}
